import { SegmentDataStructureType } from "@redotech/redo-model/marketing/segments/segment-data-structures";
import {
  CityProximityCompare,
  ComparisonType,
  ProximityCompareOperators,
  ProximityUnit,
  TokenCompareOperators,
} from "@redotech/redo-model/segments/segment-where-condition";
import { memo } from "react";
import { RedoListItem } from "../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { SegmentBuilderText } from "../segment-builder-text";
import { NumericFilterInput } from "./numeric-filter";
import { TokenHierarchyFilter } from "./token-hierarchy-filter";

const proximityCompareOperatorToFriendly: Record<
  ProximityCompareOperators,
  string
> = {
  [ProximityCompareOperators.WITHIN]: "is",
  [ProximityCompareOperators.OUTSIDE]: "is not",
};

const proximityCompareOptions: RedoListItem<ProximityCompareOperators>[] =
  Object.values(ProximityCompareOperators).map((operator) => ({
    id: operator,
    type: "text",
    text: proximityCompareOperatorToFriendly[operator],
    value: operator,
  }));

const unitOptions: RedoListItem<ProximityUnit>[] = Object.values(
  ProximityUnit,
).map((pu) => ({ id: pu, type: "text", text: pu, value: pu }));

export const CityProximityFilter = memo(function CityProximityFilter({
  comparison,
  setComparison,
  dimension,
  dataStructure,
}: {
  comparison: CityProximityCompare;
  setComparison: (condition: CityProximityCompare) => void;
  dimension: string;
  dataStructure: SegmentDataStructureType;
}) {
  return (
    <>
      <RedoSingleSelectDropdownInput<ProximityCompareOperators>
        fitToAnchor={false}
        flexGrow={false}
        options={proximityCompareOptions}
        optionSelected={({ item }) => {
          setComparison({ ...comparison, operator: item.value });
        }}
        selectedItem={proximityCompareOptions.find(
          (o) => o.value === comparison.operator,
        )}
        size="xs"
      />
      <SegmentBuilderText>within</SegmentBuilderText>
      <NumericFilterInput
        setValue={(value) =>
          setComparison({
            ...comparison,
            options: { ...comparison.options, radius: value ?? 0 },
          })
        }
        value={comparison.options.radius}
      />
      <RedoSingleSelectDropdownInput<ProximityUnit>
        fitToAnchor={false}
        flexGrow={false}
        options={unitOptions}
        optionSelected={({ item }) => {
          setComparison({
            ...comparison,
            options: { ...comparison.options, units: item.value },
          });
        }}
        selectedItem={unitOptions.find(
          (o) => o.value === comparison.options.units,
        )}
        size="xs"
      />
      <SegmentBuilderText>of</SegmentBuilderText>
      <TokenHierarchyFilter
        comparison={{
          type: ComparisonType.TOKEN,
          operator:
            comparison.operator === ProximityCompareOperators.WITHIN
              ? TokenCompareOperators.ANY
              : TokenCompareOperators.NONE,
          prerequisiteValues: comparison.prerequisiteValues,
          values: comparison.value ? [comparison.value] : [],
        }}
        dataStructure={dataStructure}
        dimension={dimension}
        multiSelect={false}
        setComparison={(hierarchyComparison) => {
          setComparison({
            ...comparison,
            operator:
              hierarchyComparison.operator === TokenCompareOperators.ANY
                ? ProximityCompareOperators.WITHIN
                : ProximityCompareOperators.OUTSIDE,
            prerequisiteValues: hierarchyComparison.prerequisiteValues,
            value: hierarchyComparison.values[0] ?? "",
          });
        }}
        showOperatorDropdown={false}
      />
    </>
  );
});
